import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import apiService from '../../services/api.service';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

type Palier = 'draft' | 'mystery' | 'announced' | 'public';
const ORDRE: Palier[] = ['draft', 'mystery', 'announced', 'public'];

/** Le palier affiché vient du serveur, qui le calcule. On ne le recalcule pas
 *  ici : deux définitions finiraient par diverger d'une seconde près. */
const palierDe = (wod: WodVisible): Palier => (wod.palier as Palier) || 'draft';

/** « 2026-10-24T16:00:00Z » → « 24/10/2026 18:00 » dans la saisie locale. */
const isoVersLocal = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const localVersIso = (v: string) => (v ? new Date(v).toISOString() : null);

/** Le composant ne dépend QUE de ce qu'il lit. cloud-admin porte deux types
 *  `WOD` distincts (types/index.ts et types/wod.types.ts) : s'accrocher à l'un
 *  des deux forcerait l'appelant à convertir, pour rien. */
interface WodVisible {
  id?: string;
  name?: string;
  palier?: Palier;
  mystery_at?: string | null;
  announced_at?: string | null;
  public_at?: string | null;
}

interface Props {
  wod: WodVisible;
  /** Rechargement après écriture : le palier vit côté serveur, pas ici. */
  onChange: () => void | Promise<void>;
}

export const WodPalier: React.FC<Props> = ({ wod, onChange }) => {
  const { t, i18n } = useTranslation();
  const actuel = palierDe(wod);

  const [programmationOuverte, setProgrammationOuverte] = useState(false);
  const [mysteryAt, setMysteryAt] = useState('');
  const [announcedAt, setAnnouncedAt] = useState('');
  const [publicAt, setPublicAt] = useState('');
  const [recul, setRecul] = useState<{ vers: Palier; impact: any } | null>(null);
  const [occupe, setOccupe] = useState(false);

  /** Le palier se DÉDUIT du temps : une bascule programmée survient sans que
   *  personne ne pousse d'événement — c'est le principe même (pas de cron, pas
   *  d'état stocké qui dérive). Sans réveil, une page ouverte à 15h59 afficherait
   *  encore « Mystère » à 16h01, et l'organisateur croirait sa programmation
   *  perdue. On ne recalcule toujours rien ici : à l'échéance, on REDEMANDE au
   *  serveur, seul à décider du palier. */
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Un WOD peut porter PLUSIEURS bascules (Mystère, Annoncé, Public). Les dates, elles,
  // ne bougent pas quand l'heure passe — seul le palier calculé change. Sans ce
  // compteur, l'effet ne se relancerait pas après la première échéance et la
  // seconde n'arriverait jamais à l'écran.
  const [reveils, setReveils] = useState(0);

  useEffect(() => {
    const echeances = [wod.mystery_at, wod.announced_at, wod.public_at]
      .map((iso) => (iso ? new Date(iso).getTime() : 0))
      .filter((t) => t > Date.now());
    if (!echeances.length) return;

    // +2 s : c'est l'horloge du SERVEUR qui décide du palier, et celle du poste
    // peut la devancer de peu. Borné, car au-delà de ~24 jours setTimeout
    // déborde et se déclencherait aussitôt.
    const delai = Math.min(Math.min(...echeances) - Date.now() + 2000, 2 ** 31 - 1);
    const minuteur = setTimeout(() => {
      void onChangeRef.current();
      setReveils((n) => n + 1); // réarme pour la bascule suivante
    }, delai);
    return () => clearTimeout(minuteur);
  }, [wod.mystery_at, wod.announced_at, wod.public_at, reveils]);

  const dateCourte = (iso?: string | null) =>
    iso
      ? new Date(iso).toLocaleString(i18n.language, {
          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
        })
      : '';

  const appliquer = async (palier: Palier) => {
    setOccupe(true);
    try {
      await apiService.setWodPalier(wod.id!, palier);
      await onChange();
      toast.success(t(`wods.palier.applique.${palier}`));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('common.error'));
    } finally {
      setOccupe(false);
      setRecul(null);
    }
  };

  /** Avancer est immédiat. RECULER passe d'abord par la vérité : ce qui est
   *  déjà parti par email ne se reprend pas, et l'écran doit le dire avant
   *  de demander un accord — pas après. */
  const choisir = async (palier: Palier) => {
    if (palier === actuel || occupe) return;
    if (ORDRE.indexOf(palier) > ORDRE.indexOf(actuel)) {
      await appliquer(palier);
      return;
    }
    setOccupe(true);
    try {
      const { data } = await apiService.wodImpactRecul(wod.id!, palier);
      setRecul({ vers: palier, impact: data });
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('common.error'));
    } finally {
      setOccupe(false);
    }
  };

  const ouvrirProgrammation = () => {
    setMysteryAt(isoVersLocal(wod.mystery_at));
    setAnnouncedAt(isoVersLocal(wod.announced_at));
    setPublicAt(isoVersLocal(wod.public_at));
    setProgrammationOuverte(true);
  };

  const enregistrerProgrammation = async () => {
    setOccupe(true);
    try {
      await apiService.setWodProgrammation(
        wod.id!, localVersIso(mysteryAt), localVersIso(announcedAt), localVersIso(publicAt),
      );
      await onChange();
      setProgrammationOuverte(false);
      toast.success(t('wods.palier.programmationEnregistree'));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('common.error'));
    } finally {
      setOccupe(false);
    }
  };

  // Les bascules ENCORE À VENIR : une date déjà passée n'est plus une
  // programmation, c'est l'état courant.
  const aVenir: string[] = [];
  const futur = (iso?: string | null) => iso && new Date(iso) > new Date();
  if (futur(wod.mystery_at)) aVenir.push(t('wods.palier.mystereLe', { date: dateCourte(wod.mystery_at) }));
  if (futur(wod.announced_at)) aVenir.push(t('wods.palier.annonceLe', { date: dateCourte(wod.announced_at) }));
  if (futur(wod.public_at)) aVenir.push(t('wods.palier.publicLe', { date: dateCourte(wod.public_at) }));

  return (
    <div className="min-w-[230px]">
      {/* Sélecteur : la sélection est un ÉTAT d'interface, donc BLANCHE.
          L'or et le dégradé sont réservés aux données de valorisation. */}
      <div className="inline-flex rounded-lg border border-border bg-background p-0.5 gap-0.5">
        {ORDRE.map((p) => (
          <button
            key={p}
            type="button"
            disabled={occupe}
            onClick={() => choisir(p)}
            className={cn(
              'px-3 py-1 text-xs rounded-md transition-colors whitespace-nowrap',
              p === actuel
                ? 'bg-white/[0.11] text-foreground font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.14)]'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t(`wods.palier.${p}`)}
          </button>
        ))}
      </div>

      {/* Ligne de planification, TOUJOURS présente — y compris pour dire qu'il
          n'y a rien. Une possibilité qu'on ne découvre qu'en cliquant au hasard
          n'existe pas : un organisateur se croirait obligé d'être devant son
          écran à l'heure de la révélation. */}
      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
        <span
          className={cn('w-1.5 h-1.5 rounded-full shrink-0',
            aVenir.length ? 'bg-primary' : 'bg-muted-foreground/30')}
        />
        {actuel === 'public' && !aVenir.length ? (
          <span>{t('wods.palier.palierFinal')}</span>
        ) : (
          <>
            <span>{aVenir.length ? aVenir.join(', ') : t('wods.palier.aucuneBascule')}</span>
            <button
              type="button"
              onClick={ouvrirProgrammation}
              className="text-foreground underline underline-offset-2 decoration-white/35 hover:decoration-white"
            >
              {aVenir.length ? t('common.edit') : t('wods.palier.programmer')}
            </button>
          </>
        )}
      </div>

      {/* ── Programmation : les bascules à venir, ensemble ─────────────── */}
      <Dialog open={programmationOuverte} onOpenChange={setProgrammationOuverte}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('wods.palier.programmerTitre', { name: wod.name })}</DialogTitle>
            <DialogDescription>{t('wods.palier.programmerAide')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {actuel === 'draft' && (
              <div>
                <Label htmlFor="wod_mystery_at">{t('wods.palier.champMystere')}</Label>
                <Input
                  id="wod_mystery_at"
                  type="datetime-local"
                  value={mysteryAt}
                  onChange={(e) => setMysteryAt(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">{t('wods.palier.aideMystere')}</p>
              </div>
            )}
            {(actuel === 'draft' || actuel === 'mystery') && (
              <div>
                <Label htmlFor="wod_announced_at">{t('wods.palier.champAnnonce')}</Label>
                <Input
                  id="wod_announced_at"
                  type="datetime-local"
                  value={announcedAt}
                  onChange={(e) => setAnnouncedAt(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">{t('wods.palier.aideAnnonce')}</p>
              </div>
            )}
            <div>
              <Label htmlFor="wod_public_at">{t('wods.palier.champPublic')}</Label>
              <Input
                id="wod_public_at"
                type="datetime-local"
                value={publicAt}
                onChange={(e) => setPublicAt(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">{t('wods.palier.aidePublic')}</p>
            </div>
            <p className="text-xs text-muted-foreground">{t('wods.palier.aucunEmail')}</p>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setMysteryAt(''); setAnnouncedAt(''); setPublicAt(''); }}
            >
              {t('wods.palier.toutRetirer')}
            </Button>
            <Button variant="outline" onClick={() => setProgrammationOuverte(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={enregistrerProgrammation} disabled={occupe}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Recul : on nomme ce qui est déjà sorti ─────────────────────── */}
      <Dialog open={!!recul} onOpenChange={(o) => !o && setRecul(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {t('wods.palier.reculTitre', {
                name: wod.name, palier: t(`wods.palier.${recul?.vers || 'draft'}`),
              })}
            </DialogTitle>
            <DialogDescription>
              {t(`wods.palier.reculEffet.${recul?.vers || 'draft'}`)}
            </DialogDescription>
          </DialogHeader>

          {!!recul?.impact?.heats_publiees && (
            <div className="rounded-lg border border-destructive/35 bg-destructive/[0.07] p-3 space-y-1">
              <p className="text-sm font-semibold text-destructive">
                {t('wods.palier.reculIrreversible')}
              </p>
              <ul className="text-sm text-muted-foreground list-disc pl-4 space-y-0.5">
                <li>{t('wods.palier.reculHeats', { count: recul.impact.heats_publiees })}</li>
                {recul.impact.athletes_ayant_recu_le_nom > 0 && (
                  <li>{t('wods.palier.reculAthletes', { count: recul.impact.athletes_ayant_recu_le_nom })}</li>
                )}
                {recul.impact.heats_sans_temoin > 0 && (
                  // Publiées avant la mise en place du témoin : on ne devine pas
                  // ce qu'elles ont dit, on le signale comme inconnu.
                  <li>{t('wods.palier.reculSansTemoin', { count: recul.impact.heats_sans_temoin })}</li>
                )}
              </ul>
            </div>
          )}
          <p className="text-sm text-muted-foreground">{t('wods.palier.reculHeatsRestent')}</p>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRecul(null)}>{t('common.cancel')}</Button>
            <Button
              variant="destructive"
              disabled={occupe}
              onClick={() => recul && appliquer(recul.vers)}
            >
              {t('wods.palier.reculConfirmer', { palier: t(`wods.palier.${recul?.vers || 'draft'}`) })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WodPalier;
